import { ReactNode, useEffect, useState } from 'react';

import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import { useIsMiddleSchoolView } from 'src/hooks/use-is-middle-school-view';
import { useNavigationNotifications } from 'src/hooks/use-navigation-notifications.hook';
import { useOnboardingUserInfo } from 'src/hooks/use-onboarding-user-info';
import { useUserAssets } from 'src/hooks/use-user-assets.hook';
import { XApiEventKeys } from 'src/interfaces/event.types';
import { getAmpConfigFlags } from 'src/services/amp-config.service';
import AuthService from 'src/services/api/auth.service';
import eventService from 'src/services/event.service';
import {
  setIsSingleSessionCodeModalOpen,
  useCurrentGradeSelector,
} from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';
import { getTheme } from 'src/utils/theme.utils';

export const Theme = ({ children }: { children: ReactNode }) => {
  const [isAmpNavV2ModuleLoaded, setIsAmpNavV2ModuleLoaded] = useState(false);

  const isMiddleSchoolView = useIsMiddleSchoolView();
  const currentGrade = useCurrentGradeSelector();
  const isTeacher = AuthService.isTeacher();
  const navigationNotifications = useNavigationNotifications();
  const notificationsToDisplay = !isTeacher
    ? navigationNotifications
    : undefined;
  const { renderAmpNavV2, updateConfig } = window.AmpNavigationV2Module ?? {};
  const dispatch = useAppDispatch();

  const { data: onboardingUserInfo, isLoading: isOnboardingUserInfoLoading } =
    useOnboardingUserInfo();
  const { isLoadingWithoutError: isUserAssetsLoading, data: userAssets } =
    useUserAssets();

  const commonXapiContextPayload = useCommonXapiContextPayloadHook();

  const handleSingleSessionCodeBtnClick = () => {
    dispatch(setIsSingleSessionCodeModalOpen(true));

    eventService.send(XApiEventKeys.classCodeModalOpened, undefined, {
      result: {
        isRightClick: false,
        ...commonXapiContextPayload,
      },
    });
  };

  // sets up a polling mechanism to detect when AmpNavigationV2Module module becomes available
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    const checkModuleLoaded = () => {
      if (window.AmpNavigationV2Module) {
        setIsAmpNavV2ModuleLoaded(true);
        clearInterval(interval);
      }
    };

    if (!isAmpNavV2ModuleLoaded) {
      interval = setInterval(checkModuleLoaded, 100);
    }

    return () => {
      clearInterval(interval);
    };
  }, [isAmpNavV2ModuleLoaded]);

  useEffect(() => {
    const { isNewNavigationEnabled } = getAmpConfigFlags();

    // @TODO: stop checking isNewNavigationEnabled after RFS 2025 (new nav release)
    if (
      isNewNavigationEnabled &&
      isAmpNavV2ModuleLoaded &&
      window.AmpNavigationV2Module &&
      renderAmpNavV2
    ) {
      const kc = AuthService.getKC();
      renderAmpNavV2('amp-nav-v2-container', {
        _keycloak: kc,
        pageTitle: 'Home',
        enableSalesforceChatSessionCleaning: true,
        onSingleSessionCodeBtnClick: handleSingleSessionCodeBtnClick,
        isStudentPreviewMode: isTeacher,
        // set undefined for students to allow amp-nav-v2 to do its own grade checks
        isStudentMiddleSchoolView: isTeacher ? isMiddleSchoolView : undefined,
        notificationsToDisplay,
        apiData: {
          retail: {
            userAssets: {
              data: userAssets ?? [],
              isLoading: isUserAssetsLoading,
            },
          },
          onboarding: {
            userInfo: {
              data: onboardingUserInfo,
              isLoading: isOnboardingUserInfoLoading,
            },
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAmpNavV2ModuleLoaded]);

  useEffect(() => {
    if (!updateConfig) return;

    updateConfig({
      isStudentPreviewMode: isTeacher,
      // set undefined for students to allow amp-nav-v2 to do its own grade checks
      isStudentMiddleSchoolView: isTeacher ? isMiddleSchoolView : undefined,
      notificationsToDisplay,
    });
  }, [isMiddleSchoolView, updateConfig, isTeacher, notificationsToDisplay]);

  return (
    <div
      className={getTheme(currentGrade, isMiddleSchoolView)}
      data-testid="theme-container"
    >
      {children}
    </div>
  );
};
